import { PersistService } from './persist';

export const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export type CampaignKey = (typeof CAMPAIGN_KEYS)[number];
export type CampaignParams = Record<string, string>;

type CampaignSource = Record<string, unknown> | null | undefined;

const MAX_CAMPAIGN_VALUE_LENGTH = 255;
const RETURN_TO_ORIGIN = 'https://proxy-cheap.invalid';

function normalizeCampaignValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const normalized = value.trim();

  if (!normalized) return null;

  return normalized.slice(0, MAX_CAMPAIGN_VALUE_LENGTH);
}

function getFirstCampaignValue(searchParams: URLSearchParams, key: CampaignKey): string | null {
  return (
    searchParams
      .getAll(key)
      .map(normalizeCampaignValue)
      .find((value): value is string => value !== null) ?? null
  );
}

function getCampaignValue(source: CampaignSource, key: CampaignKey): string | null {
  return normalizeCampaignValue(source?.[key]);
}

export function extractCampaignParams(searchParams: URLSearchParams): CampaignParams {
  return CAMPAIGN_KEYS.reduce<CampaignParams>((params, key) => {
    const value = getFirstCampaignValue(searchParams, key);

    if (value !== null) {
      params[key] = value;
    }

    return params;
  }, {});
}

export function mergeCampaignParams(...sources: CampaignSource[]): CampaignParams {
  return CAMPAIGN_KEYS.reduce<CampaignParams>((params, key) => {
    const value = sources.map((source) => getCampaignValue(source, key)).find((item) => item !== null);

    if (value !== undefined && value !== null) {
      params[key] = value;
    }

    return params;
  }, {});
}

export function normalizeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || !value || value?.trim() !== value || !value.startsWith('/')) {
    return null;
  }

  const hasControlCharacter = Array.from(value).some(
    (character) => character.charCodeAt(0) <= 31 || character.charCodeAt(0) === 127,
  );

  if (value.startsWith('//') || value.includes('\\') || hasControlCharacter) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(value);

    if (decoded.startsWith('//') || decoded.includes('\\')) {
      return null;
    }

    const parsed = new URL(value, RETURN_TO_ORIGIN);

    if (parsed.origin !== RETURN_TO_ORIGIN || parsed.protocol !== 'https:') {
      return null;
    }
  } catch {
    return null;
  }

  return value;
}

export function extractCampaignParamsFromReturnTo(value: unknown): CampaignParams {
  const normalizedReturnTo = normalizeReturnTo(value);

  if (!normalizedReturnTo) return {};

  return extractCampaignParams(new URL(normalizedReturnTo, RETURN_TO_ORIGIN).searchParams);
}

export function stripCampaignParamsFromReturnTo(value: unknown): string | null {
  const normalizedReturnTo = normalizeReturnTo(value);

  if (!normalizedReturnTo) return null;

  const returnUrl = new URL(normalizedReturnTo, RETURN_TO_ORIGIN);

  CAMPAIGN_KEYS.forEach((key) => {
    returnUrl.searchParams.delete(key);
  });

  return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
}

export function resolveCampaignParams(searchParams: URLSearchParams, storedParams?: CampaignSource): CampaignParams {
  return mergeCampaignParams(
    extractCampaignParams(searchParams),
    PersistService.get<boolean>('auth.justSignedOut')
      ? undefined
      : extractCampaignParamsFromReturnTo(searchParams.get('returnTo')),
    storedParams,
  );
}

export function buildAuthSearch(searchParams: URLSearchParams, storedParams?: CampaignSource): string {
  const params = new URLSearchParams();
  const returnTo = normalizeReturnTo(searchParams.get('returnTo'));
  const campaignParams = resolveCampaignParams(searchParams, storedParams);

  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  CAMPAIGN_KEYS.forEach((key) => {
    const value = campaignParams[key];

    if (value) {
      params.set(key, value);
    }
  });

  return params.toString();
}
